import {useEffect, useMemo, useState} from 'react';
import {useApolloClient, useMutation, useQuery} from '@apollo/client';
import {Button, Col, Form, Input, Row, Select, Switch, Tabs} from 'antd';
import SubmitButton from 'components/Startup/components/SubmitButton';
import DBModal from 'components/modal/modal';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {defaultLanguages} from 'pages/news/newsTypes';
import {toast} from 'react-toastify';
import {
  GET_ALL_SUBSCRIPTION_PLANS,
  GET_PLAN_SUBSCRIPTIONS_CATALOGS,
  UPSERT_SUBSCRIPTION_BUNDLE,
} from '../resolvers';
import styles from '../subscriptionSettings.module.scss';
import {
  humanizeValue,
  normalizeBundleKey,
} from '../subscriptions/BundleHelpers';
import BundleItemsSection from './bundleModal/BundleItemsSection';
import BundlePricesSection from './bundleModal/BundlePricesSection';
import CatalogModalSection from './catalogItemModal/CatalogModalSection';

const languageOptions = defaultLanguages.map(language => ({
  value: language.key,
  label: language.label,
}));

const mapBundleToFormValues = bundle => {
  const firstMultilang = bundle?.multilang?.[0];
  const itemSelections = (bundle?.items || []).map(item => ({
    selectedPlanKey: item?.catalogItem?.plan ?? undefined,
    selectedCatalogKey: item?.catalogKey ?? undefined,
  }));

  return {
    name: bundle?.name ?? '',
    description: bundle?.description ?? '',
    language: bundle?.language ?? 'EN',
    isVisibleToUsers: Boolean(bundle?.isVisibleToUsers),
    isEnabled: Boolean(bundle?.isEnabled),
    multilangLanguage: firstMultilang?.language || undefined,
    multilangName: firstMultilang?.name || '',
    multilangDescription: firstMultilang?.description || '',
    bundlePrices: (bundle?.prices || []).length
      ? (bundle?.prices || []).map(price => ({
          currency: price?.currency || undefined,
          amount:
            price?.amount === null || price?.amount === undefined
              ? undefined
              : Number(price?.amount),
        }))
      : [{currency: undefined, amount: undefined}],
    itemSelections: itemSelections.length
      ? itemSelections
      : [
          {
            selectedPlanKey: undefined,
            selectedCatalogKey: undefined,
          },
        ],
  };
};

const buildBundlePayload = (values, existingBundle) => {
  const uniqueLanguages =
    values.multilangLanguage &&
    values.multilangName &&
    values.multilangDescription
      ? [
          {
            language: values.multilangLanguage,
            name: values.multilangName?.trim(),
            description: values.multilangDescription?.trim(),
          },
        ]
      : [];

  const uniqueCatalogKeys = [];
  const keySeen = {};
  (values.itemSelections || []).forEach(row => {
    const catalogKey = row?.selectedCatalogKey;
    if (!catalogKey || keySeen[catalogKey]) {
      return;
    }
    keySeen[catalogKey] = true;
    uniqueCatalogKeys.push({catalogKey});
  });

  const uniquePrices = [];
  const priceSeen = {};
  (values.bundlePrices || []).forEach(price => {
    const currency = price?.currency;
    const amount = Number(price?.amount);
    if (!currency || Number.isNaN(amount)) {
      return;
    }
    const key = `${currency}-${amount}`;
    if (priceSeen[key]) {
      return;
    }
    priceSeen[key] = true;
    uniquePrices.push({
      currency,
      amount,
    });
  });

  return {
    key: existingBundle?.key || normalizeBundleKey(values.name),
    name: values.name?.trim(),
    description: values.description?.trim(),
    language: values.language,
    multilang: uniqueLanguages,
    prices: uniquePrices,
    items: uniqueCatalogKeys,
    isVisibleToUsers: Boolean(values.isVisibleToUsers),
    isEnabled: Boolean(values.isEnabled),
  };
};

const SubscriptionBundleModal = ({
  isOpen,
  onClose,
  bundleData,
  refetch,
  canSubmit,
}) => {
  const [form] = Form.useForm();
  const apolloClient = useApolloClient();
  const isEditMode = Boolean(bundleData?.key);
  const [catalogByPlan, setCatalogByPlan] = useState({});
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const itemSelections = Form.useWatch('itemSelections', form) || [];

  const {data: plansData} = useQuery(GET_ALL_SUBSCRIPTION_PLANS, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [upsertBundle, {loading: saving}] = useMutation(
    UPSERT_SUBSCRIPTION_BUNDLE,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );
  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_TEXT,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const primaryLanguage = Form.useWatch('language', form);
  const multilangLanguage = Form.useWatch('multilangLanguage', form);
  const bundleName = Form.useWatch('name', form);
  const bundleDescription = Form.useWatch('description', form);
  const multilangLanguageLabel =
    languageOptions.find(option => option.value === multilangLanguage)?.label ||
    humanizeValue(multilangLanguage);

  const plansResponse = plansData?.getAllSubscriptionPlans;
  useEffect(() => {
    if (plansResponse?.__typename === 'Error') {
      toast.error(plansResponse.message);
    }
  }, [plansResponse]);

  const planOptions = useMemo(() => {
    const plans =
      plansResponse?.__typename === 'ResponseWithDabaSubscriptionPlans'
        ? plansResponse.data || []
        : [];

    return plans.map(plan => ({
      value: plan?.key,
      label: plan?.name || plan?.key,
    }));
  }, [plansResponse]);

  const fetchCatalogForPlan = async planKey => {
    if (!planKey || catalogByPlan[planKey]) {
      return;
    }

    setLoadingCatalog(true);
    try {
      const {data} = await apolloClient.query({
        query: GET_PLAN_SUBSCRIPTIONS_CATALOGS,
        variables: {filter: {plan: planKey}},
        fetchPolicy: 'no-cache',
      });

      const response = data?.getSubscriptionPlansCatalog;
      if (response?.__typename === 'Error') {
        toast.error(response.message);
        return;
      }

      const items =
        response?.__typename === 'ResponseWithSubscriptionCatalog'
          ? response.data || []
          : [];

      setCatalogByPlan(prev => ({
        ...prev,
        [planKey]: items,
      }));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingCatalog(false);
    }
  };

  useEffect(() => {
    const keys = {};
    itemSelections.forEach(row => {
      if (row?.selectedPlanKey) {
        keys[row.selectedPlanKey] = true;
      }
    });
    Object.keys(keys).forEach(fetchCatalogForPlan);
  }, [itemSelections]);

  const getCatalogItemOptions = planKey => {
    const items = catalogByPlan[planKey] || [];
    return items
      .filter(item => item?.key)
      .map(item => {
        const baseLabel = item?.label?.EN || humanizeValue(item.key);
        const disabled = item?.isEnabled === false;
        return {
          value: item.key,
          label: disabled ? `${baseLabel} (disabled)` : baseLabel,
          disabled,
        };
      });
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isEditMode) {
      form.setFieldsValue(mapBundleToFormValues(bundleData));
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      name: '',
      description: '',
      language: 'EN',
      isVisibleToUsers: false,
      isEnabled: true,
      multilangLanguage: undefined,
      multilangName: '',
      multilangDescription: '',
      itemSelections: [
        {
          selectedPlanKey: undefined,
          selectedCatalogKey: undefined,
        },
      ],
      bundlePrices: [{currency: undefined, amount: undefined}],
    });
  }, [isOpen, isEditMode, bundleData, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handlePlanChange = (index, planKey) => {
    fetchCatalogForPlan(planKey);
    form.setFields([
      {name: ['itemSelections', index, 'selectedPlanKey'], value: planKey},
      {name: ['itemSelections', index, 'selectedCatalogKey'], value: undefined},
    ]);
  };

  const handleCatalogItemChange = (index, catalogKey) => {
    form.setFields([
      {
        name: ['itemSelections', index, 'selectedCatalogKey'],
        value: catalogKey,
      },
    ]);
  };

  const handleSubmit = values => {
    const seenRows = {};
    const hasDuplicateBundleRows = (values.itemSelections || []).some(row => {
      const key = row?.selectedCatalogKey || '';
      if (!key) {
        return false;
      }
      if (seenRows[key]) {
        return true;
      }
      seenRows[key] = true;
      return false;
    });

    if (hasDuplicateBundleRows) {
      toast.error('Duplicate bundle row selected. Pick unique catalog items.');
      return;
    }

    const payload = buildBundlePayload(values, bundleData);

    upsertBundle({
      variables: {
        input: payload,
      },
    })
      .then(({data}) => {
        const response = data?.upsertSubscriptionBundle;
        if (response?.__typename === 'Error') {
          toast.error(response.message);
          return;
        }

        toast.success(response?.message || 'Bundle saved');
        handleClose();
        if (refetch) {
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message || 'Save failed');
      });
  };

  const handleTranslate = async () => {
    const sourceLang = form.getFieldValue('language');
    const targetLang = form.getFieldValue('multilangLanguage');
    const sourceName = form.getFieldValue('name');
    const sourceDescription = form.getFieldValue('description');

    if (!sourceLang || !targetLang || !sourceName || !sourceDescription) {
      toast.error(
        'Select languages and fill name/description before translate',
      );
      return;
    }

    try {
      const nameResult = await translateText({
        variables: {
          sourceLang,
          targetLang,
          sourceText: sourceName,
        },
      });
      const descriptionResult = await translateText({
        variables: {
          sourceLang,
          targetLang,
          sourceText: sourceDescription,
        },
      });

      const nameResponse = nameResult?.data?.translateText;
      const descriptionResponse = descriptionResult?.data?.translateText;

      if (
        nameResponse?.__typename === 'Error' ||
        descriptionResponse?.__typename === 'Error' ||
        !nameResponse?.targetText ||
        !descriptionResponse?.targetText
      ) {
        toast.error('Error Translating Text');
        return;
      }

      form.setFieldsValue({
        multilangName: nameResponse.targetText,
        multilangDescription: descriptionResponse.targetText,
      });
      toast.success('Translated Successfully');
    } catch (error) {
      toast.error(error.message || 'Error Translating Text');
    }
  };

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={handleClose}
      destroyOnClose
      width={860}
      content={
        <div className={styles['catalog-modal']}>
          <div className={styles['catalog-modal-header']}>
            <h1>{isEditMode ? 'Edit bundle' : 'Create bundle'}</h1>
          </div>

          <Form
            form={form}
            layout="vertical"
            className={`${styles['catalog-modal-form']} ${styles['bundle-form-narrow']}`}
            onFinish={handleSubmit}
            onFinishFailed={() => toast.error('Fix validation errors')}>
            <CatalogModalSection
              title="Bundle identity"
              description="Name, default language, and flags for users.">
              <Tabs
                tabBarExtraContent={
                  multilangLanguage ? (
                    <Button
                      type="default"
                      disabled={
                        loadingTranslation || !bundleName || !bundleDescription
                      }
                      onClick={handleTranslate}>
                      Translate
                    </Button>
                  ) : null
                }>
                <Tabs.TabPane key="original" tab="Original">
                  <Row gutter={12}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Name"
                        name="name"
                        rules={[{required: true, message: 'Name required'}]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Description"
                        name="description"
                        rules={[
                          {required: true, message: 'Description required'},
                        ]}>
                        <Input.TextArea rows={1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Primary language"
                        name="language"
                        rules={[
                          {
                            required: true,
                            message: 'Primary language required',
                          },
                        ]}>
                        <Select options={languageOptions} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Translation language"
                        name="multilangLanguage">
                        <Select
                          allowClear
                          placeholder="Select language for translation tab"
                          options={languageOptions.filter(
                            option => option.value !== primaryLanguage,
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Visible to users"
                        name="isVisibleToUsers"
                        valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Enabled"
                        name="isEnabled"
                        valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                </Tabs.TabPane>
                {multilangLanguage ? (
                  <Tabs.TabPane key="multilang" tab={multilangLanguageLabel}>
                    <Row gutter={12}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Name"
                          name="multilangName"
                          rules={[{required: true, message: 'Name required'}]}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Description"
                          name="multilangDescription"
                          rules={[
                            {required: true, message: 'Description required'},
                          ]}>
                          <Input.TextArea rows={1} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                ) : null}
              </Tabs>
            </CatalogModalSection>

            <BundleItemsSection
              planOptions={planOptions}
              loadingCatalog={loadingCatalog}
              onPlanChange={handlePlanChange}
              onCatalogItemChange={handleCatalogItemChange}
              getCatalogItemOptions={getCatalogItemOptions}
            />
            <BundlePricesSection />

            <div className={styles['catalog-modal-actions']}>
              <SubmitButton
                disabled={saving || !canSubmit}
                label={isEditMode ? 'Save' : 'Create'}
              />
            </div>
          </Form>
        </div>
      }
    />
  );
};

export default SubscriptionBundleModal;
